import { MenuView } from 'features/MenuDetail/models/viewMenu';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import { COUPON_CLASSIFICATIONS } from '../constants';
import { MenuDetail } from '../models/menu';
import './style.scss';

interface MenuTableProps {
  menuDetails: MenuDetail[];
  handleCheckBox: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRestore: (id: number) => void;
  checkboxesRef: React.RefObject<Array<HTMLInputElement>>; // Defining the prop type for the ref
}

function MenuListTable({ menuDetails, handleCheckBox, handleRestore, checkboxesRef }: MenuTableProps) {
  const { t } = useTranslation();
  const [lstMenuDetails, setLstMenuDetails] = useState<Array<MenuDetail>>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string }>({ key: '', direction: '' });
  const requestSort = (key: string) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: string) => {
    // Default: if no sort key, highlight start_date as descending
    if (!sortConfig.key && key === 'start_date') {
      return <FaSortDown size={16} />;
    }
    if (sortConfig.key !== key)
      return (
      <>
        <FaSort size={16} />
      </>
      );
    return sortConfig.direction === 'ascending' ? <FaSortUp size={16} /> : <FaSortDown size={16} />;
  };

  useEffect(() => {
    const sortableItems = menuDetails.map((menuDetail) => {
        // Extract menu_properties from menuDetail
        const menuProperties = menuDetail.menu.menu_properties || [];
        // Map to a flat object for easier access
        let lunch = false;
        let set_meal = false;
        let age_limit = false;
        let the_clerk_calls_out = false;
        let coupon = false;
        let service = false;
        let delivery_timing = false;
        let eos = false;
        menuProperties.reduce((acc, prop) => {
          // Map Japanese property key to config key
          switch (prop.property) {
            case 1:
                lunch = prop.is_active;
                break;
            case 2:
              set_meal = prop.is_active;
              break;
            case 3:
              age_limit = prop.is_active;
              break;
            case 4:
              the_clerk_calls_out = prop.is_active;
              break;
            case 5:
              coupon = prop.is_active;
              break;
            case 6:
              service = prop.is_active;
              break;
            case 7:
              delivery_timing = prop.is_active;
              break;
            case 8:
              eos = prop.is_active;
              break;
            default:
              break;
          }
          return acc;
        }, {} as Record<string, boolean>);
        // Attach to menuDetail
        return {
          ...menuDetail,
          lunch,
          set_meal,
          age_limit,
          the_clerk_calls_out,
          coupon,
          service,
          delivery_timing,
          eos,
        };
      });
    setLstMenuDetails(sortableItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuDetails]);

  const sortedMenuDetails = useMemo(() => {
    let sortableItems = [...lstMenuDetails];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;
        switch (sortConfig.key) {
          case 'menu_classification':
            const aName = a.menu.food_system_menu.menu_classification?.name || '';
            const bName = b.menu.food_system_menu.menu_classification?.name || '';
            return sortConfig.direction === 'ascending'
              ? aName.localeCompare(bName, 'ja')
              : bName.localeCompare(aName, 'ja');
          case 'menu_name':
            const aMenuName = a.menu.food_system_menu.name || '';
            const bMenuName = b.menu.food_system_menu.name || '';
            return sortConfig.direction === 'ascending'
              ? aMenuName.localeCompare(bMenuName, 'ja')
              : bMenuName.localeCompare(aMenuName, 'ja');
          case 'menu_code':
            aValue = a.menu.food_system_menu.cd + (COUPON_CLASSIFICATIONS[a.menu.coupon_classification] || '') || '';
            bValue = b.menu.food_system_menu.cd + (COUPON_CLASSIFICATIONS[b.menu.coupon_classification] || '') || '';
            return sortConfig.direction === 'ascending'
              ? String(aValue).localeCompare(String(bValue))
              : String(bValue).localeCompare(String(aValue));
          case 'flg':
            aValue = a.menu.is_sub;
            bValue = b.menu.is_sub;
            return sortConfig.direction === 'ascending'
              ? Number(aValue) - Number(bValue)
              : Number(bValue) - Number(aValue);
          case 'display_timing':
            aValue = a.menu.display_timing;
            bValue = b.menu.display_timing;
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
          case 'lunch':
            aValue = a.lunch ?? false;
            bValue = b.lunch ?? false;
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
          case 'set_meal':
            aValue = a.set_meal ?? false;
            bValue = b.set_meal ?? false;
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
          case 'age_limit':
            aValue = a.age_limit ?? false;
            bValue = b.age_limit ?? false;
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
          case 'the_clerk_calls_out':
            aValue = a.the_clerk_calls_out ?? false;
            bValue = b.the_clerk_calls_out ?? false;
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
          case 'coupon':
            aValue = a.coupon ?? false;
            bValue = b.coupon ?? false;
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
          case 'service':
            aValue = a.service ?? false;
            bValue = b.service ?? false;
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
          case 'delivery_timing':
            aValue = a.delivery_timing ?? false;
            bValue = b.delivery_timing ?? false;
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
          case 'eos':
            aValue = a.eos ?? false;
            bValue = b.eos ?? false;
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
          case 'start_date':
            aValue = a.validity_period?.split('~')[0]?.trim() || '';
            bValue = b.validity_period?.split('~')[0]?.trim() || '';
            // Compare dates
            return sortConfig.direction === 'ascending'
              ? new Date(aValue).getTime() - new Date(bValue).getTime()
              : new Date(bValue).getTime() - new Date(aValue).getTime();
          case 'end_date':
            aValue = a.validity_period?.split('~')[1]?.trim() || '';
            bValue = b.validity_period?.split('~')[1]?.trim() || '';
            // Compare dates
            return sortConfig.direction === 'ascending'
              ? new Date(aValue).getTime() - new Date(bValue).getTime()
              : new Date(bValue).getTime() - new Date(aValue).getTime();
          default:
            return 0;
        }
      });
    }
    return sortableItems;
  }, [lstMenuDetails, sortConfig]);


  return (
    <>
      <div className="sticky-table" style={{ maxHeight: '500px' }}>
        <Table bordered>
          <thead className="text-center" style={{ backgroundColor: '#36489a', color: '#fff' }}>
            <tr>
              <th rowSpan={3} className="align-middle">
                {t('MenuList.Table.selection')}
              </th>
              <th
                rowSpan={3}
                className="cursor-pointer"
                style={{ cursor: 'pointer' }}
                onClick={() => requestSort('menu_classification')}
              >
                <div className="d-flex align-items-center">
                  <span className="flex-fill">{t('MenuList.Table.menu_classification')}</span>
                  {getSortIcon('menu_classification')}
                </div>
              </th>
              <th rowSpan={3} className="cursor-pointer" onClick={() => requestSort('menu_code')}>
                <div className="d-flex align-items-center">
                  <span className="flex-fill">
                    {t('MenuList.Table.menu_code')}
                    <br />
                    {t('MenuList.Table.classification')}
                    <br />
                    {t('MenuList.Table.classification_2')}
                  </span>
                  {getSortIcon('menu_code')}
                </div>
              </th>
              <th rowSpan={3} className="cursor-pointer" onClick={() => requestSort('menu_name')}>
                <div className="d-flex align-items-center" style={{textAlign: 'center'}}>
                  <span className="flex-fill">{t('MenuList.Table.menu_name')}</span>
                  {getSortIcon('menu_name')}
                </div>
              </th>
              <th rowSpan={3} className="cursor-pointer" onClick={() => requestSort('flg')}>
                <div className="d-flex align-items-center">
                  <span className="flex-fill">{t('MenuList.Table.flg')}</span>
                  {getSortIcon('flg')}
                </div>
              </th>
              <th rowSpan={3} className="cursor-pointer" onClick={() => requestSort('display_timing')}>
                <div className="d-flex align-items-center">
                  <span className="flex-fill">{t('MenuList.Table.display_timing')}</span>
                  {getSortIcon('display_timing')}
                </div>
              </th>
              <th rowSpan={2} colSpan={8}>
                {t('MenuList.Table.property')}
              </th>
              <th colSpan={3}>{t('MenuList.Table.pos_information')}</th>
            </tr>
            <tr>
              <th colSpan={2}>{t('MenuList.Filter.date_of_expires')}</th>
              <th rowSpan={2}>{t('MenuList.Table.pos_code')}</th>
            </tr>
            <tr>
              <th rowSpan={2} className="cursor-pointer" onClick={() => requestSort('lunch')}>
                {t('MenuList.Table.lunch')}
                {getSortIcon('lunch')}
              </th>
              <th rowSpan={2} className="cursor-pointer" onClick={() => requestSort('set_meal')}>
                {t('MenuList.Table.set_meal')}
                {getSortIcon('set_meal')}
              </th>
              <th rowSpan={2} className="cursor-pointer" onClick={() => requestSort('age_limit')}>
                {t('MenuList.Table.age_limit')}
                {getSortIcon('age_limit')}
              </th>
              <th rowSpan={2} className="cursor-pointer" onClick={() => requestSort('the_clerk_calls_out')}>
                {t('MenuList.Table.the_clerk_calls_out')}
                {getSortIcon('the_clerk_calls_out')}
              </th>
              <th rowSpan={2} className="cursor-pointer" onClick={() => requestSort('coupon')}>
                {t('MenuList.Table.coupon')}
                {getSortIcon('coupon')}
              </th>
              <th rowSpan={2} className="cursor-pointer" onClick={() => requestSort('service')}>
                {t('MenuList.Table.service')}
                {getSortIcon('service')}
              </th>
              <th rowSpan={2} className="cursor-pointer" onClick={() => requestSort('delivery_timing')}>
                {t('MenuList.Table.delivery_timing')}
                {getSortIcon('delivery_timing')}
              </th>
              <th rowSpan={2} className="cursor-pointer" onClick={() => requestSort('eos')}>
                {t('MenuList.Table.eos')}
                {getSortIcon('eos')}
              </th>
              <th rowSpan={2} className="cursor-pointer" onClick={() => requestSort('start_date')}>
                {t('MenuList.Table.start')}
                {getSortIcon('start_date')}
              </th>
              <th rowSpan={2} className="cursor-pointer" onClick={() => requestSort('end_date')}>
                {t('MenuList.Table.end')}
                {getSortIcon('end_date')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedMenuDetails && sortedMenuDetails.length > 0 ? (
              sortedMenuDetails.map((menu_detail, index) => {
                let menu_view = new MenuView(
                  menu_detail.menu,
                  menu_detail.start_date.toString(),
                  menu_detail.end_date.toString()
                );
                let menu_properties_active = menu_view.properties_active();

                let currClassification = menu_detail.menu.food_system_menu.menu_classification;
                let classBorder = '';
                if (index > 0) {
                  const lastClassification = lstMenuDetails[index - 1].menu.food_system_menu.menu_classification;
                  classBorder =
                    currClassification && lastClassification && currClassification.cd !== lastClassification.cd
                      ? 'my-top-border'
                      : '';
                }

                let noneDeletedClass = 'text-center align-middle ';
                const show_flg = menu_detail.menu.is_sub ? '〇' : 'ー';

                // Split validity_period into start and end
                let validityStart = '';
                let validityEnd = '';
                if (menu_detail.validity_period) {
                  const [start, end] = menu_detail.validity_period.split('~').map((s) => s.trim());
                  validityStart = start || '';
                  validityEnd = end || '';
                }

                return (
                  <tr
                    key={index}
                    className={menu_detail.delete_flg ? 'deleted_row ' + noneDeletedClass : noneDeletedClass}
                  >
                    <td className={classBorder}>
                      {menu_detail.delete_flg && !menu_detail.menu.food_system_menu.delete_flg ? (
                        <Button
                          type="button"
                          onClick={() => {
                            handleRestore(menu_detail.menu.id);
                          }}
                          className="btn btn-sm btn-block btn-warning"
                        >
                          {t('MenuList.Button.restore')}
                        </Button>
                      ) : !menu_detail.menu.food_system_menu.delete_flg ? (
                        <Form.Check
                          type="checkbox"
                          onChange={handleCheckBox}
                          style={{ fontSize: '1.5rem' }}
                          id={menu_detail.id.toString()}
                          ref={(element: HTMLInputElement) => {
                            if (checkboxesRef.current) {
                              checkboxesRef.current[index] = element;
                            }
                          }}
                        ></Form.Check>
                      ) : (
                        <div></div>
                      )}
                    </td>
                    <td className={classBorder}>{currClassification ? currClassification.name : ''}</td>
                    <td className={classBorder}>
                      {menu_view.menu.food_system_menu.cd}
                      {COUPON_CLASSIFICATIONS[menu_view.menu.coupon_classification] || ''}
                    </td>
                    <td className={classBorder}>{menu_view.menu.food_system_menu.name}</td>
                    <td className={classBorder}>{show_flg}</td>
                    <td className={classBorder}>{menu_view.menu_display_timing_name}</td>
                    {menu_properties_active &&
                      menu_properties_active.map((prop, idx) => {
                        return (
                          <td className={classBorder} key={idx}>
                            {prop?.sign}
                          </td>
                        );
                      })}
                    <td className={classBorder}>{validityStart}</td>
                    <td className={classBorder}>{validityEnd}</td>
                    <td className={classBorder} style={{ padding: 0 }}>
                      <Table className="table-child">
                        <tbody>
                          {menu_detail.not_expired_menu_pos.map((pos, idx) => (
                            <tr key={idx}>
                              <td
                                className={
                                  menu_detail.delete_flg ? 'deleted_row ' + noneDeletedClass : noneDeletedClass
                                }
                              >
                                {pos}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr></tr>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
}

export default MenuListTable;
